import chevronRightIcon from "@tabler/icons/outline/chevron-right.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate, useMatch } from "react-router-dom";

import List, { ListItem } from "@/components/list";
import { CardTitle, HStack, Icon, Stack, Text } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { cn } from "@/utils/cn";

const messages = defineMessages({
  addEmail: {
    id: "settings.add_email",
    defaultMessage: "Verify your email for support and account recovery.",
  },
  blocks: { id: "settings.blocks", defaultMessage: "Blocks" },
  changeEmail: { id: "settings.change_email", defaultMessage: "Change Email" },
  changePassword: {
    id: "settings.change_password",
    defaultMessage: "Change Password",
  },
  configureMfa: {
    id: "settings.configure_mfa",
    defaultMessage: "Configure MFA",
  },
  deleteAccount: {
    id: "settings.delete_account",
    defaultMessage: "Delete Account",
  },
  editProfile: { id: "settings.edit_profile", defaultMessage: "Edit Profile" },
  importData: {
    id: "navigation_bar.import_data",
    defaultMessage: "Import data",
  },
  mfaDisabled: { id: "mfa.disabled", defaultMessage: "Disabled" },
  mfaEnabled: { id: "mfa.enabled", defaultMessage: "Enabled" },
  messages: { id: "settings.messages", defaultMessage: "Direct Messages" },
  mutes: { id: "settings.mutes", defaultMessage: "Mutes" },
  other: { id: "settings.other", defaultMessage: "Other Options" },
  preferences: { id: "settings.preferences", defaultMessage: "Preferences" },
  privacy: { id: "settings.privacy", defaultMessage: "Privacy" },
  discoverability: {
    id: "settings.discoverability",
    defaultMessage: "Discoverability",
  },
  advertisers: { id: "settings.advertisers", defaultMessage: "Advertisers" },
  profile: { id: "settings.profile", defaultMessage: "Profile" },
  security: { id: "settings.security", defaultMessage: "Security" },
  sessions: { id: "settings.sessions", defaultMessage: "Active sessions" },
  settings: { id: "settings.settings", defaultMessage: "Settings" },
  subscriptions: {
    id: "settings.subscriptions",
    defaultMessage: "Subscriptions",
  },
});

interface SettingsPageProps {
  children: React.ReactNode;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const match = useMatch("/settings");

  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();

  const isMainPage = !!match;

  const navigateToPreferences = () => navigate("/settings/preferences");
  const navigateToPrivacy = () => navigate("/settings/privacy");
  const navigateToSecurity = () => navigate("/settings/security");
  const navigateToMessages = () => navigate("/settings/messages");
  const navigateToProfile = () => navigate("/settings/profile");
  const navigateToOther = () => navigate("/settings/other");
  const navigateToSubscriptions = () => navigate("/settings/subscriptions");

  if (!currentAccount) return null;

  return (
    <div className="md:col-span-12 lg:col-span-9">
      <div className="border-x bg-background text-foreground lg:h-screen lg:overflow-hidden">
        <div className="grid h-full grid-cols-9 divide-x">
          <Stack
            className={cn(
              "col-span-9 overflow-hidden bg-background lg:col-span-3",
              {
                "hidden lg:flex": !isMainPage,
              },
            )}
          >
            <Stack space={4} className="h-full">
              <Stack space={4} className="p-4 lg:pt-4">
                <HStack alignItems="center" justifyContent="between">
                  <CardTitle title={intl.formatMessage(messages.settings)} />
                </HStack>
              </Stack>

              <Stack className="h-full grow px-4">
                {!currentAccount.source.email_verified && (
                  <button
                    className="mb-4 flex w-full items-center justify-between space-x-4 rounded-lg bg-secondary-500/10 p-4 text-left"
                    onClick={() => openModal("ADD_EMAIL")}
                    data-testid="add-email"
                  >
                    <Text
                      className="text-secondary-500 dark:text-secondary-500"
                      size="sm"
                      weight="medium"
                    >
                      {intl.formatMessage(messages.addEmail)}
                    </Text>
                    <Icon
                      src={chevronRightIcon}
                      className="text-secondary-500"
                    />
                  </button>
                )}

                <List>
                  <ListItem
                    label={intl.formatMessage(messages.preferences)}
                    onClick={navigateToPreferences}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.privacy)}
                    onClick={navigateToPrivacy}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.security)}
                    onClick={navigateToSecurity}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.subscriptions)}
                    onClick={navigateToSubscriptions}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.messages)}
                    onClick={navigateToMessages}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.profile)}
                    onClick={navigateToProfile}
                  />
                  <ListItem
                    label={intl.formatMessage(messages.other)}
                    onClick={navigateToOther}
                  />
                </List>
              </Stack>
            </Stack>
          </Stack>

          <Stack className="col-span-9 h-full lg:col-span-6 lg:overflow-auto">
            {children}
          </Stack>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
